import { useState } from "react";
import { MapPin, Clock, MessageCircle, Send, Instagram, Music2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Reveal, MaskedLine, SectionHeading } from "@/components/Reveal";
import { BOOKING_SERVICES } from "@/data/static";

const EMPTY = { name: "", whatsapp: "", service: "", date: "", notes: "" };

export default function Contact() {
  const { settings, waLink } = useSettings();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.whatsapp || !form.service || !form.date) {
      toast.error("Mohon lengkapi nama, nomor WhatsApp, layanan, dan tanggal.");
      return;
    }
    const dateStr = new Date(form.date).toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    const msg = [
      "Halo Salon Strawberry Official! Saya ingin booking:",
      "",
      `Nama: ${form.name}`,
      `No. WhatsApp: ${form.whatsapp}`,
      `Layanan: ${form.service}`,
      `Tanggal: ${dateStr}`,
      form.notes ? `Catatan: ${form.notes}` : null,
    ].filter(Boolean).join("\n");
    window.open(waLink(msg), "_blank", "noopener,noreferrer");
    setSending(true);
    api.post("/bookings", form).catch(() => {}).finally(() => setSending(false));
    toast.success("Booking terkirim! Anda diarahkan ke WhatsApp admin.");
    setForm(EMPTY);
  };

  const inputCls = "w-full rounded-sm border border-brand-gold/30 bg-white px-4 py-3.5 text-sm text-brand-ink placeholder:text-brand-ink-muted/50 outline-none transition-colors duration-300 focus:border-brand-gold focus:ring-1 focus:ring-brand-gold/40";

  return (
    <div data-testid="contact-page" className="pt-32 pb-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <MaskedLine delay={0.1}><p className="overline-label">Contact & Booking</p></MaskedLine>
        <h1 className="mt-4 font-cinzel font-black text-brand-burgundy text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          <MaskedLine delay={0.25}>RESERVASI</MaskedLine>
          <MaskedLine delay={0.4} innerClassName="text-gold-gradient">MOMENT ANDA.</MaskedLine>
        </h1>

        <div className="mt-16 grid gap-14 lg:grid-cols-[1.1fr_0.9fr]">
          <Reveal>
            <form onSubmit={submit} data-testid="booking-form" className="rounded-sm bg-white gold-foil-border p-8 sm:p-10 shadow-[0_15px_50px_rgba(74,14,23,0.08)]">
              <SectionHeading index="01" overline="Booking Form" title="Isi Detail Reservasi" />
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-brand-burgundy">Nama Lengkap *</label>
                  <input data-testid="booking-name-input" value={form.name} onChange={set("name")} placeholder="Nama Anda" className={inputCls} required />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-brand-burgundy">Nomor WhatsApp *</label>
                  <input data-testid="booking-whatsapp-input" value={form.whatsapp} onChange={set("whatsapp")} placeholder="08xxxxxxxxxx" type="tel" className={inputCls} required />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-brand-burgundy">Pilih Layanan *</label>
                  <select data-testid="booking-service-select" value={form.service} onChange={set("service")} className={inputCls} required>
                    <option value="">— Pilih layanan —</option>
                    {BOOKING_SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-brand-burgundy">Pilih Tanggal *</label>
                  <input data-testid="booking-date-input" value={form.date} onChange={set("date")} type="date" min={new Date().toISOString().split("T")[0]} className={inputCls} required />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-brand-burgundy">Catatan / Request Stylist</label>
                  <textarea data-testid="booking-notes-textarea" value={form.notes} onChange={set("notes")} rows={4} placeholder="Contoh: request stylist favorit, alergi produk, preferensi ruangan hijab..." className={`${inputCls} resize-none`} />
                </div>
              </div>
              <button
                data-testid="booking-submit-btn"
                type="submit"
                disabled={sending}
                className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-brand-burgundy py-4 text-xs font-bold uppercase tracking-[0.22em] text-brand-cream transition-all duration-300 hover:bg-brand-gold hover:text-brand-ink disabled:opacity-60"
              >
                <Send size={15} /> {sending ? "Mengirim..." : "Booking via WhatsApp"}
              </button>
              <p className="mt-4 text-center text-xs text-brand-ink-muted">Setelah klik booking, Anda langsung diarahkan ke WhatsApp admin kami.</p>
            </form>
          </Reveal>

          <div className="space-y-8">
            <Reveal delay={0.15}>
              <div className="rounded-sm bg-brand-burgundy p-8 sm:p-10" data-testid="contact-info-card">
                <p className="overline-label !text-brand-gold-warm">Kunjungi Kami</p>
                <div className="mt-6 space-y-5">
                  <p className="flex gap-4 text-sm leading-relaxed text-brand-cream/85"><MapPin size={18} className="mt-0.5 shrink-0 text-brand-gold" />{settings?.address || "Jl. Mekar Puspita No.100, Mekarwangi, Kec. Bojongloa Kidul, Kota Bandung, Jawa Barat 40236"}</p>
                  <p className="flex gap-4 text-sm text-brand-cream/85"><Clock size={18} className="mt-0.5 shrink-0 text-brand-gold" />{settings?.hours || "Senin - Sabtu: 08.00 - 19.00 WIB • Minggu: 10.00 - 19.00 WIB"}</p>
                  <a data-testid="contact-wa-direct" href={waLink("Halo Salon Strawberry Official!")} target="_blank" rel="noopener noreferrer" className="flex gap-4 text-sm text-brand-cream/85 transition-colors hover:text-brand-gold">
                    <MessageCircle size={18} className="mt-0.5 shrink-0 text-brand-gold" /> Chat langsung dengan admin
                  </a>
                </div>
                <div className="mt-8 flex gap-3 border-t border-brand-gold/20 pt-7">
                  {[
                    { icon: Instagram, href: settings?.instagram_url, id: "instagram" },
                    { icon: Music2, href: settings?.tiktok_url, id: "tiktok" },
                    { icon: ShoppingBag, href: settings?.shopee_url, id: "shopee" },
                  ].map(({ icon: Icon, href, id }) => (
                    <a key={id} data-testid={`contact-social-${id}`} href={href || "#"} target="_blank" rel="noopener noreferrer" className="flex h-10 w-10 items-center justify-center rounded-full border border-brand-gold/30 text-brand-gold transition-all duration-300 hover:bg-brand-gold hover:text-brand-ink">
                      <Icon size={16} />
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="overflow-hidden rounded-sm gold-foil-border shadow-[0_15px_50px_rgba(74,14,23,0.12)]" data-testid="contact-map">
                <iframe
                  title="Lokasi Salon Strawberry Official"
                  src={settings?.maps_embed_url || "https://www.google.com/maps?q=Jl.+Mekar+Puspita+No.100,+Mekarwangi,+Kec.+Bojongloa+Kidul,+Kota+Bandung&output=embed"}
                  className="h-72 w-full"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}
